import { memo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  ScrollArea,
} from '@flote/ui';
import { TeamForm, type TeamFormData } from './TeamForm';
import type { Hub, Team, Driver, TeamMembers } from '@flote/contracts/http';

interface TeamFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TeamFormData) => Promise<void>;
  isProcessing?: boolean;
  hubs: Hub[];
  drivers: Driver[];
  team?: Team;
  teamMembers?: TeamMembers | null;
}

export const TeamFormModal = memo<TeamFormModalProps>(({
  open,
  onOpenChange,
  onSubmit,
  isProcessing = false,
  hubs,
  drivers,
  team,
  teamMembers,
}) => {
  const isEditMode = !!team;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isProcessing) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isProcessing]
  );

  const initialData = team
    ? {
        name: team.name,
        hub: team.hub || undefined,
        enableSelfAssignment: team.enableSelfAssignment,
        workers: teamMembers?.workerIds ?? [],
        managers: teamMembers?.managerIds ?? [],
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[90vh] p-0 sm:w-full">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{isEditMode ? 'Edit Team' : 'Create Team'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update team settings and hub assignment'
              : 'Create a new team for organizing drivers and deliveries'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="px-6 pb-6">
            <TeamForm
              onSubmit={onSubmit}
              isProcessing={isProcessing}
              hubs={hubs}
              drivers={drivers}
              initialData={initialData}
              mode={isEditMode ? 'edit' : 'create'}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});

TeamFormModal.displayName = 'TeamFormModal';
